import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { endpoints } from '../_config/endpoint';
import { GlobalService } from './global.service';
import { StaffStore } from '@iq-central-front-end/share';
import { catchError, tap } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import {
  DashboardFilter,
  InitialDashboardFilter,
} from '../interfaces/dashboard.interface';
import { DashboardStore } from 'libs/share/src/lib/store/dashboard.store';
import { full } from '@cloudinary/url-gen/qualifiers/fontHinting';

@Injectable({
  providedIn: 'root',
})
export class StaffService {
  constructor(
    private http: HttpClient,
    private _global: GlobalService,
    private staffStore: StaffStore,
    private dashboardStore: DashboardStore
  ) { }

  createEnterpriseUser(enterpriseUserRequestDTO) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.createEnterpriseUser}`,
      enterpriseUserRequestDTO
    ).pipe(
      tap((response) => {
        if (response['responseResult']) {
          const staff = {
            ...response['responseResult'],
            user: {
              fullName: `${response['responseResult'].fullName}`,
              avatarUrl: `${response['responseResult']?.avatarUrl}`,
              id: `${response['responseResult']?.id}`,
              phoneNumber: `${response['responseResult']?.phoneNumber}`,
              username: `${response['responseResult']?.username}`
            }
          }
          this.staffStore.addStaff(staff);
        }
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getAllEnterpriseUsers() {
    const hasExistingData = this.staffStore.staffs$()?.data?.length > 0;
    if (!hasExistingData) {
      this.staffStore.setLoading(true);
    }

    const pageNumber = this.staffStore.currentPage$();
    const pageSize = this.staffStore.pageSize$();
    const search = this.staffStore.searchQuery$();

    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize)
      .set('searchParams', search ?? '');

    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAllEnterpriseStaffs}`,
      { params }
    ).pipe(
      tap((response) => {
        const paginationResponse = {
          data: response['responseResult'].users,
          total: response['responseResult'].paginationInfo.totalElements,
          page: response['responseResult'].paginationInfo.number,
          limit: response['responseResult'].paginationInfo.size
        };
        this.staffStore.setStaffs(paginationResponse);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getStaffsEnrolledToCourse(courseCode) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.get(
      `${environment.API_URL_V1}/api/course/${courseCode}/enrolled-users`
    ).pipe(
      tap((response) => {
        this.staffStore.setEnrolledStaffs(response['responseResult'] || []);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getActiveEnterpriseUsers(pageNumber, pageSize) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getActiveEnterpriseStaffs}`,
      { params }
    ).pipe(
      tap((response) => {
        const paginationResponse = {
          data: response['responseResult'].users,
          total: response['responseResult'].paginationInfo.totalElements,
          page: response['responseResult'].paginationInfo.currentPage,
          limit: response['responseResult'].paginationInfo.pageSize
        };
        this.staffStore.setActiveStaffs(paginationResponse);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getUserProfile() {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.get(`${environment.API_URL_V1}/${endpoints.profile}`).pipe(
      tap((response) => {
        this.staffStore.setStaff(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  updateEnterpriseUser(payload) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.put(
      `${environment.API_URL_V1}/${endpoints.updateEnterpriseUser}`,
      payload
    ).pipe(
      tap((response) => {
        if (response['responseResult']) {
          this.staffStore.updateStaff(response['responseResult']);
        }
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  uploadAvatar(formData) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.uploadAvatar}`,
      formData
    ).pipe(
      tap(() => {
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  uploadStaffAvatar(formData, staffId) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.uploadUserAvatar}/${staffId}/upload_avatar`,
      formData
    ).pipe(
      tap(() => {
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  toggleActivateDeactivateStaff(id) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    const payload = {};
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.activateDeactivateStaff}/${id}/toggle_activation`,
      payload
    ).pipe(
      tap(() => {
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getOneEnterpriseUser(id) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getOneEnterpriseUser}/${id}`
    ).pipe(
      tap((response) => {
        this.staffStore.setStaff(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  updateOneEnterpriseUser(updatedInfo, staffId) {
    this.staffStore.setLoading(true);
    this.staffStore.clearError();

    return this.http.put(
      `${environment.API_URL_V1}/${endpoints.updateOneEnterpriseUser}/${staffId}/update`,
      updatedInfo
    ).pipe(
      tap((response) => {
        if (response['responseResult']) {
          this.staffStore.updateStaff(response['responseResult']);
        }
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getAssignedCourses(
    payload: {
      pageNumber, pageSize, searchQuery
    }
  ) {
    const hasExistingData = this.staffStore.assignedCourses$()?.assignedCourses?.length > 0;
    if (!hasExistingData) {
      this.staffStore.setLoading(true);
    }

    const params = new HttpParams()
      .set('page', payload.pageNumber)
      .set('size', payload.pageSize)
      .set('searchParams', payload.searchQuery);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.assignedCourses}/assigned_courses`,
      { params }
    ).pipe(
      tap((response) => {
        this.staffStore.setAssignedCourses(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getAssignedLearningPath(
    payload: {
      pageNumber, pageSize, searchQuery
    }
  ) {
    const hasExistingData = this.staffStore.assignedLearningPaths$()?.learningPaths?.length > 0;
    if (!hasExistingData) {
      this.staffStore.setLoading(true);
    }

    const params = new HttpParams()
      .set('page', payload.pageNumber)
      .set('size', payload.pageSize)
      .set('searchParams', payload.searchQuery);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.assignedLearningPaths}/assigned_learning_paths`,
      { params }
    ).pipe(
      tap((response) => {
        this.staffStore.setAssignedLearningPaths(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.message || 'An error occurred');
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }
  getStaffAssignedCourses(staffId, pageNumber, pageSize) {
    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.assignedCourses}/${staffId}/assigned_courses`,
      { params }
    );
  }

  getStaffAssignedLearningPath(staffId, pageNumber, pageSize) {
    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.assignedLearningPaths}/${staffId}/assigned_learning_paths`,
      { params }
    );
  }

  getEnrolledCourses(
    payload: {
      pageNumber, pageSize, searchQuery
    }
  ) {
    const hasExistingData = this.staffStore.enrolledCourses$()?.courses?.length > 0;
    if (!hasExistingData) {
      this.staffStore.setLoading(true);
    }

    const params = new HttpParams()
      .set('page', payload.pageNumber)
      .set('size', payload.pageSize)
      .set('searchParams', payload.searchQuery);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getEnrolledCourses}`,
      { params }
    ).pipe(
      tap((response) => {
        this.staffStore.setEnrolledCourses(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.error.responseMessage);
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getSavedCourses(
    payload: {
      pageNumber, pageSize, searchQuery
    }
  ) {
    const hasExistingData = this.staffStore.savedCourses$()?.courses?.length > 0;
    if (!hasExistingData) {
      this.staffStore.setLoading(true);
    }

    const params = new HttpParams()
      .set('page', payload.pageNumber)
      .set('size', payload.pageSize)
      .set('searchParams', payload.searchQuery);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.savedCourses}`,
      { params }
    ).pipe(
      tap((response) => {
        this.staffStore.setSavedCourses(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.error.responseMessage);
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getLikedCourses(
    payload: {
      pageNumber, pageSize, searchQuery
    }
  ) {
    const hasExistingData = this.staffStore.likedCourses$()?.courses?.length > 0;
    if (!hasExistingData) {
      this.staffStore.setLoading(true);
    }

    const params = new HttpParams()
      .set('page', payload.pageNumber)
      .set('size', payload.pageSize)
      .set('searchParams', payload.searchQuery);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.likedCourses}`,
      { params }
    ).pipe(
      tap((response) => {
        this.staffStore.setLikedCourses(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.error.responseMessage);
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  sendMessage(payload, staffId) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.sendMessage}/${staffId}/messages/send`,
      payload
    );
  }

  getDashboardContents(filterParam?: DashboardFilter): Observable<any> {
    const hasExistingData = this.dashboardStore.dashboard$();
    if (!hasExistingData) {
      this.dashboardStore.setLoading(true);
    }

    if (!filterParam) {
      filterParam = InitialDashboardFilter;
    }

    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.getDashboardContents}`,
      filterParam
    ).pipe(
      tap((response) => {
        this.dashboardStore.setDashboard(response['responseResult']);
        this.dashboardStore.setLoading(false);
      }),
      catchError((error) => {
        this.dashboardStore.setError(error.error.responseMessage);
        this.dashboardStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getDueUsers(payload: DashboardFilter) {
    const hasExistingData = this.staffStore.dueUsers$()?.length > 0;
    if (!hasExistingData) {
      this.staffStore.setLoading(true);
    }

    return this.http.post(
      `${environment.API_URL_V1}/api/enterprise/due-users`,
      payload
    ).pipe(
      tap((response) => {
        this.staffStore.setDueUsers(response['responseResult']);
        this.staffStore.setLoading(false);
      }),
      catchError((error) => {
        this.staffStore.setError(error.error.responseMessage);
        this.staffStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  setWeeklyGoal(payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.setWeeklyGoal}`,
      payload
    );
  }
}
